import { DETECTOR, type DetectedBox } from "../camera/detector"
import { EMBEDDER_URL, HEAD_URL } from "./head"
import { createEngine, SEGMENTER, type ViewReading } from "./vision-engine"
import type { FrameRequest, FrameResult, FromWorker, ToWorker, VisionCaps } from "./vision-protocol"
import { runFrame } from "./vision-run"
import { wasmBase } from "./vision"

// The camera's vision, from the page: in a worker when the browser can
// (the camera never stutters), else the same engine on the page. One per
// page; null when it can't run here at all (the camera then goes guided,
// and photos are checked by the server alone).

export interface VisionClient {
  mode: "worker" | "inline"
  caps: VisionCaps
  // How often the live camera may look (slower on a slow phone).
  intervalMs: number
  // One look at the live camera. Only one at a time: while one is out, this
  // returns null at once (the caller just skips that frame). Null too when
  // the look failed.
  frame(request: FrameRequest): Promise<FrameResult | null>
  // The car(s) in one picture (a gallery photo). Closes it.
  detect(image: ImageBitmap): Promise<DetectedBox[]>
  // The brain's read of a photo taken or picked.
  photo(photo: Blob): Promise<ViewReading | null>
  // Once, if the engine stops answering (crashed or stuck): from then on
  // every answer is "none", and the camera should go guided.
  onLost(listener: () => void): () => void
  // Done with it: frees the models' memory.
  dispose(): void
}

// Past these the worker counts as stuck (a frame takes well under a second;
// a photo waits behind any frame already in the worker).
const TIMEOUT = { FRAME_MS: 8000, DETECT_MS: 10_000, PHOTO_MS: 15_000 } as const

function closeRequest(r: FrameRequest) {
  r.detect.close()
  r.segment?.image.close()
  r.view?.close()
}

async function inlineClient(): Promise<VisionClient | null> {
  const engine = await createEngine({ wasmBase: wasmBase() })
  if (!engine) return null
  let busy = false
  let off = false
  let lost = false
  const listeners = new Set<() => void>()
  return {
    mode: "inline",
    caps: engine.caps,
    intervalMs: engine.intervalMs,
    async frame(request) {
      if (busy || off) {
        closeRequest(request)
        return null
      }
      busy = true
      try {
        return runFrame(engine, request)
      } catch {
        return null
      } finally {
        busy = false
        // Failing every frame: let it go, and say so.
        if (!off && engine.broken()) {
          off = true
          lost = true
          engine.dispose()
          for (const listener of listeners) listener()
        }
      }
    },
    async detect(image) {
      try {
        return off ? [] : engine.detect(image, performance.now())
      } catch {
        return []
      } finally {
        image.close()
      }
    },
    photo: async (photo) => (off ? null : engine.readPhoto(photo)),
    onLost(listener) {
      if (lost) queueMicrotask(listener)
      else listeners.add(listener)
      return () => listeners.delete(listener)
    },
    dispose() {
      if (off) return
      off = true
      engine.dispose()
    },
  }
}

// MediaPipe in a worker draws on an OffscreenCanvas with WebGL 2: where the
// browser has none (iPhones before iOS 17), the page runs it instead.
function workerCanRun(): boolean {
  if (typeof Worker === "undefined" || typeof createImageBitmap !== "function" || typeof OffscreenCanvas === "undefined") {
    return false
  }
  try {
    const gl = new OffscreenCanvas(1, 1).getContext("webgl2")
    if (!gl) return false
    gl.getExtension("WEBGL_lose_context")?.loseContext()
    return true
  } catch {
    return false
  }
}

function workerClient(): Promise<VisionClient | null> {
  return new Promise((resolve) => {
    let worker: Worker
    try {
      worker = new Worker(new URL("./vision-worker.ts", import.meta.url), { type: "module" })
    } catch {
      resolve(null)
      return
    }
    let next = 0
    let inFlight = false
    let started = false
    let stopped = false
    let wasLost = false
    const listeners = new Set<() => void>()
    type Waiting = { done: (msg: FromWorker | null) => void; ms: number; timer: ReturnType<typeof setTimeout> | null }
    const waiting = new Map<number, Waiting>()

    // Stops the worker and answers everything still waiting with "none".
    const stop = (lost: boolean) => {
      if (stopped) return
      stopped = true
      wasLost = lost && started
      worker.terminate()
      document.removeEventListener("visibilitychange", onVisibility)
      for (const w of waiting.values()) {
        if (w.timer) clearTimeout(w.timer)
        w.done(null)
      }
      waiting.clear()
      if (!started) resolve(null)
      else if (lost) for (const listener of listeners) listener()
    }
    // A question's time limit runs only while the page is on screen (a
    // locked phone pauses everything; the answer comes when it's back).
    const arm = (w: Waiting) => {
      if (w.timer) clearTimeout(w.timer)
      w.timer = document.visibilityState === "hidden" ? null : setTimeout(() => stop(true), w.ms)
    }
    const onVisibility = () => {
      for (const w of waiting.values()) arm(w)
    }
    document.addEventListener("visibilitychange", onVisibility)
    const ask = (msg: Extract<ToWorker, { id: number }>, transfer: Transferable[], ms: number) =>
      new Promise<FromWorker | null>((done) => {
        if (stopped) {
          done(null)
          return
        }
        const w: Waiting = { done, ms, timer: null }
        waiting.set(msg.id, w)
        arm(w)
        try {
          worker.postMessage(msg, transfer)
        } catch {
          if (w.timer) clearTimeout(w.timer)
          waiting.delete(msg.id)
          done(null)
        }
      })

    // No time limit on starting: the models can take a while on a slow line,
    // and the page would only have to fetch them again. A worker that can't
    // start says so (an error, or no caps) and the page runs the engine.
    worker.onerror = (e) => {
      e.preventDefault()
      stop(true)
    }
    worker.onmessageerror = () => stop(true)
    worker.onmessage = (e: MessageEvent<FromWorker>) => {
      const msg = e.data
      if (msg.type === "lost") {
        stop(true)
        return
      }
      if (msg.type === "ready") {
        if (!msg.caps) {
          stop(false)
          return
        }
        started = true
        resolve({
          mode: "worker",
          caps: msg.caps,
          intervalMs: msg.intervalMs,
          async frame(request) {
            if (inFlight || stopped) {
              closeRequest(request)
              return null
            }
            inFlight = true
            const transfer: Transferable[] = [request.detect]
            if (request.segment) transfer.push(request.segment.image)
            if (request.view) transfer.push(request.view)
            try {
              const reply = await ask({ type: "frame", id: ++next, request }, transfer, TIMEOUT.FRAME_MS)
              if (!reply) closeRequest(request)
              return reply?.type === "frame" ? reply.result : null
            } finally {
              inFlight = false
            }
          },
          async detect(image) {
            const reply = await ask({ type: "detect", id: ++next, image }, [image], TIMEOUT.DETECT_MS)
            if (!reply) image.close()
            return reply?.type === "detect" ? reply.detections : []
          },
          async photo(photo) {
            const reply = await ask({ type: "photo", id: ++next, photo }, [], TIMEOUT.PHOTO_MS)
            return reply?.type === "photo" ? reply.reading : null
          },
          onLost(listener) {
            // Already gone (before anyone was listening): say so now.
            if (wasLost) queueMicrotask(listener)
            else listeners.add(listener)
            return () => listeners.delete(listener)
          },
          dispose: () => stop(false),
        })
        return
      }
      const w = waiting.get(msg.id)
      if (!w) return
      if (w.timer) clearTimeout(w.timer)
      waiting.delete(msg.id)
      w.done(msg)
    }
    worker.postMessage({ type: "init", wasmBase: wasmBase() } satisfies ToWorker)
  })
}

let loading: Promise<VisionClient | null> | null = null

// `inline`: run on the page (a dev override for tests, ?camvision=inline).
export function loadVision(options: { inline?: boolean } = {}): Promise<VisionClient | null> {
  if (!loading) {
    const attempt = (async () => {
      const viaWorker = !options.inline && workerCanRun()
      return (viaWorker ? await workerClient() : null) ?? (await inlineClient())
    })()
    loading = attempt
    void attempt.then((client) => {
      if (loading !== attempt) return
      // A failed load can be tried again later (the network came back).
      if (!client) loading = null
      // One that stopped answering stays off for this visit (a phone short
      // on memory would only lose it again).
      else client.onLost(() => {
        if (loading === attempt) loading = Promise.resolve(null)
      })
    })
  }
  return loading
}

// The photos are done: free the models (they load again if the camera opens).
export function releaseVision(): void {
  const current = loading
  loading = null
  void current?.then((client) => client?.dispose())
}

// Warm the HTTP cache with the models and the runtime while the person types
// their details (phone only, idle time, never on data saver / 2G).
export function prefetchVision(): void {
  const nav = navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }
  if (nav.connection?.saveData || /2g/.test(nav.connection?.effectiveType ?? "")) return
  const go = () => {
    for (const url of [
      DETECTOR.MODEL_URL,
      EMBEDDER_URL,
      HEAD_URL,
      SEGMENTER.MODEL_URL,
      `${wasmBase()}/vision_wasm_internal.wasm`,
      `${wasmBase()}/vision_wasm_internal.js`,
    ]) {
      void fetch(url, { priority: "low" } as RequestInit).catch(() => {})
    }
  }
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback
  if (idle) idle(go)
  else setTimeout(go, 1500)
}

